import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import type { CreateEventData } from "../../../services/api/financial.service";

export interface EventFormProps {
  onSubmit: (data: CreateEventData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  onRevise?: () => void;
  canRevise?: boolean;
  initialData?: Partial<CreateEventData>;
  isLoading?: boolean;
  isEditing?: boolean;
}

const EVENT_TYPES = [
  "funding",
  "launch", 
  "milestone",
  "asset",
  "other",
] as const;

const EVENT_ICONS = [
  "DollarSign",
  "Rocket",
  "Target",
  "Calendar",
  "TrendingUp",
  "Award",
  "Zap",
  "Flag",
  "Users",
  "Building",
];

const EVENT_COLORS = [
  "#ef4444", // red
  "#f59e0b", // amber
  "#10b981", // green
  "#3b82f6", // blue
  "#8b5cf6", // purple
  "#06b6d4", // cyan
];

const EventForm: React.FC<EventFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  onRevise,
  canRevise = false,
  initialData,
  isLoading = false,
  isEditing = false,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateEventData>({
    defaultValues: {
      date: initialData?.date || new Date().toISOString().split("T")[0],
      type: initialData?.type || "milestone",
      priority: initialData?.priority || "medium",
      description: initialData?.description || "",
      amount: initialData?.amount || undefined,
      icon: initialData?.icon || "Calendar",
      color: initialData?.color || EVENT_COLORS[0],
      // Asset-specific fields
      monthlyAppreciationRate: initialData?.monthlyAppreciationRate 
        ? initialData.monthlyAppreciationRate * 100 // Convert decimal to percentage for display
        : undefined,
      minimumValue: initialData?.minimumValue || undefined,
      capacityGroupId: initialData?.capacityGroupId || "",
      projectionId: initialData?.projectionId || "",
      capacityIncrease: initialData?.capacityIncrease || undefined,
    },
  });

  const selectedType = watch("type");

  // Auto-assign color and icon based on type and priority
  React.useEffect(() => {
    if (selectedType && !initialData?.color) {
      const typeIndex = EVENT_TYPES.indexOf(selectedType);
      const colorIndex = typeIndex >= 0 ? typeIndex % EVENT_COLORS.length : 0;
      setValue("color", EVENT_COLORS[colorIndex]);
    }

    if (selectedType && !initialData?.icon) {
      const iconMap = {
        funding: "DollarSign",
        launch: "Rocket",
        milestone: "Target",
        asset: "TrendingUp",
        other: "Calendar",
      };
      setValue("icon", iconMap[selectedType] || "Calendar");
    }
  }, [selectedType, initialData, setValue]);

  const handleFormSubmit = (data: CreateEventData) => {
    // Convert numbers and handle asset-specific fields
    const processedData = {
      ...data,
      amount: data.amount ? Number(data.amount) : undefined,
      monthlyAppreciationRate: data.monthlyAppreciationRate 
        ? Number(data.monthlyAppreciationRate) / 100 // Convert percentage to decimal
        : undefined,
      minimumValue: data.minimumValue ? Number(data.minimumValue) : undefined,
    };
    onSubmit(processedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormField label="Event Description" error={errors.description?.message}>
        <Input
          {...register("description", { required: "Description is required" })}
          placeholder="e.g., Series A Funding Round, Product Launch"
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Type" error={errors.type?.message}>
          <select
            {...register("type", { required: "Type is required" })}
            className="sh-select"
          >
            <option value="funding">Funding</option>
            <option value="launch">Launch</option>
            <option value="milestone">Milestone</option>
            <option value="asset">Asset</option>
            <option value="other">Other</option>
          </select>
        </FormField>

        <FormField label="Priority" error={errors.priority?.message}>
          <select
            {...register("priority", { required: "Priority is required" })}
            className="sh-select"
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </FormField>
      </div>

      <FormField label="Date" error={errors.date?.message}>
        <Input
          type="date"
          {...register("date", { required: "Date is required" })}
        />
      </FormField>

      <FormField
        label={selectedType === "asset" ? "Initial Asset Value" : "Amount (Optional)"}
        error={errors.amount?.message}
        helperText={
          selectedType === "asset" 
            ? "Enter the initial value of the asset"
            : "For funding events, specify the funding amount"
        }
      >
        <Input
          type="number"
          step="0.01"
          {...register("amount", {
            min: { value: 0, message: "Amount must be positive" },
            valueAsNumber: true,
            required: selectedType === "asset" ? "Asset value is required" : false,
          })}
          placeholder="0.00"
        />
      </FormField>

      {/* Asset-specific fields */}
      {selectedType === "asset" && (
        <>
          <FormField
            label="Monthly Appreciation Rate (%)"
            error={errors.monthlyAppreciationRate?.message}
            helperText="Per month, not annual. Enter 6 for 6%/month, 0.5 for 0.5%/month. Use negative values for depreciation (e.g. -2 for 2%/month)."
          >
            <Input
              type="number"
              step="0.01"
              {...register("monthlyAppreciationRate", {
                required: "Appreciation rate is required for assets",
                valueAsNumber: true,
              })}
              placeholder="0.5"
            />
          </FormField>

          <FormField
            label="Minimum Value (Optional)"
            error={errors.minimumValue?.message}
            helperText="Salvage value - asset will not depreciate below this amount (defaults to 0)"
          >
            <Input
              type="number"
              step="0.01"
              {...register("minimumValue", {
                min: { value: 0, message: "Minimum value must be positive" },
                valueAsNumber: true,
              })}
              placeholder="0.00"
            />
          </FormField>
        </>
      )}

      {selectedType === "milestone" ? (
        <div className="space-y-4 rounded-[14px] border border-white/10 p-4">
          <FormHelpCallout
            learnMoreHref="/help/capacity-groups"
            learnMoreLabel="Milestone capacity increases"
          >
            Raise a shared capacity group or a single projection&apos;s cap on
            this date — e.g. office expansion or new providers.
          </FormHelpCallout>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Capacity increase"
              helperText="Additional customers supported from this date forward."
            >
              <Input
                type="number"
                {...register("capacityIncrease", {
                  min: { value: 0, message: "Must be 0 or greater" },
                  valueAsNumber: true,
                })}
                placeholder="Additional customers supported"
              />
            </FormField>
            <FormField
              label="Capacity group ID (optional)"
              helperText="Firestore id of the shared capacity group to expand."
            >
              <Input {...register("capacityGroupId")} placeholder="Shared group id" />
            </FormField>
          </div>
          <FormField
            label="Projection ID (optional)"
            helperText="Or target one customer projection instead of a shared group."
          >
            <Input {...register("projectionId")} placeholder="Specific projection id" />
          </FormField>
        </div>
      ) : null}

      <FormField label="Icon" error={errors.icon?.message}>
        <select
          {...register("icon")}
          className="sh-select"
        >
          {EVENT_ICONS.map((icon) => (
            <option key={icon} value={icon}>
              {icon}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Color" error={errors.color?.message}>
        <div className="flex items-center gap-2">
          <input
            type="color"
            {...register("color")}
            className="w-12 h-10 border border-white/10 rounded cursor-pointer"
          />
          <Input
            {...register("color")}
            placeholder="#ef4444"
            className="flex-1"
          />
        </div>
      </FormField>

      {isEditing && selectedType === "asset" && canRevise && onRevise ? (
        <FormHelpCallout>
          Changing balance or rate here rewrites history. To keep past months as
          modeled,{" "}
          <button
            type="button"
            className="font-medium text-sky-300 underline underline-offset-2"
            onClick={onRevise}
          >
            revise from a date
          </button>{" "}
          instead.
        </FormHelpCallout>
      ) : null}

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading
            ? isEditing
              ? "Updating..."
              : "Adding..."
            : isEditing
            ? "Update Event"
            : "Add Event"}
        </Button>
        {isEditing && onDelete && (
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={onDelete}
            disabled={isLoading}
          >
            Delete
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export { EventForm };